/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-09-03
 */

import { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Send as SendIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';

// Add local dependencies.
import Header from '../../layout/Header';
import { StudioHeader } from '../../navigation/studio';
import {
  useGetStudioQuery,
  useGetInvitationMetricsQuery,
  useGetStudioMembersQuery,
} from '../../../features/studios/studiosApi';
import { useStudioPermissions } from '../../../hooks/useStudioPermissions';
import { useStudioUserLimits } from '../../../hooks/useSubscriptionLimits';
import { useGetCurrentSubscriptionQuery } from '../../../features/auth/authApi';
import { MemberStatus } from '../../../types/studioMembers';
import LiveRegion from '../../common/LiveRegion';
import { ARIA_ROLES } from '../../../utils/accessibility';
import InvitationList from '../../studios/invitations/InvitationList';
import InviteMemberForm from '../../studios/invitations/InviteMemberForm';
import UsageIndicator from '../../subscription/UsageIndicator';
import { YouTubeVideoButton } from '../../common';

/**
 * Studio invitations page component.
 * Displays and manages studio invitations with metrics and management capabilities.
 * Only accessible to studio owners.
 *
 * @example
 * return (
 *   <InvitationsPage />
 * )
 * @returns {React.ReactElement} component.
 */
const InvitationsPage: React.FC = () => {
  const { studioId } = useParams<{ studioId: string }>();
  const navigate = useNavigate();
  const [showInviteForm, setShowInviteForm] = useState(false);

  // Fetch studio data
  const {
    data: studio,
    isLoading: isLoadingStudio,
    error: studioError,
  } = useGetStudioQuery(studioId!, {
    skip: !studioId,
  });

  // Fetch invitation metrics
  const { data: metrics, refetch: refetchMetrics } =
    useGetInvitationMetricsQuery(studioId!, {
      skip: !studioId,
    });

  // Get total active member count (unfiltered) for usage calculation
  const { data: totalMembersResponse } = useGetStudioMembersQuery(
    {
      studioId: studioId!,
      search: '', // No search filter for total count
      role: undefined, // No role filter for total count
      status: MemberStatus.ACTIVE, // Only count active members
      page: 1,
      pageSize: 1, // We only need the count, not the actual members
    },
    {
      skip: !studioId,
    }
  );

  const currentMemberCount = totalMembersResponse?.count || 0;
  const { canAddUser, isAtUserLimit, userLimitPercent } = useStudioUserLimits(
    studioId,
    currentMemberCount
  );

  // Get subscription data for actual member limit
  const { data: subscription } = useGetCurrentSubscriptionQuery();
  const maxUsers = subscription?.plan.max_users_per_studio || 0;

  // Check permissions
  const {
    permissions,
    role,
    isLoading: isLoadingPermissions,
  } = useStudioPermissions();

  // Handle loading states
  if (isLoadingStudio || isLoadingPermissions) {
    return (
      <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
        <Header />
        <StudioHeader loading />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <LiveRegion
            message="Loading studio invitations, please wait..."
            politeness="POLITE"
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '50vh',
              gap: 2,
            }}
          >
            <CircularProgress size={50} />
            <Typography variant="h6" color="text.secondary">
              Loading invitations...
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  // Handle errors
  if (studioError || !studio) {
    return (
      <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
        <Header />
        <StudioHeader studio={null} />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <LiveRegion
            message="Error loading studio information. Please try refreshing the page."
            politeness="ASSERTIVE"
          />
          <Alert severity="error" sx={{ mb: 4 }} role="alert">
            {studioError &&
            'status' in studioError &&
            studioError.status === 404
              ? 'Studio not found. Please check the URL and try again.'
              : 'Failed to load studio information. Please try refreshing the page.'}
          </Alert>
        </Container>
      </Box>
    );
  }

  // Check if user has permission to manage invitations
  if (!permissions?.canInviteMembers) {
    return (
      <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
        <Header />
        <StudioHeader
          studio={studio}
          userRole={role}
          permissions={permissions}
        />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <LiveRegion
            message="Access denied. You don't have permission to manage invitations."
            politeness="ASSERTIVE"
          />
          <Alert severity="warning" sx={{ mb: 4 }} role="alert">
            You don&apos;t have permission to manage invitations for this
            studio. Only studio owners can send and manage invitations.
          </Alert>
        </Container>
      </Box>
    );
  }

  const handleInvitationSuccess = () => {
    setShowInviteForm(false);
    refetchMetrics();
  };

  const handleInvitationUpdate = () => {
    refetchMetrics();
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
      <Header />
      <StudioHeader studio={studio} userRole={role} permissions={permissions} />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Live region for successful data load */}
        <LiveRegion
          message={`Studio invitations loaded successfully for ${studio.name}`}
          politeness="POLITE"
        />

        {/* Page Content */}
        <Box
          role={ARIA_ROLES.MAIN}
          aria-label="Studio invitations management"
          sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}
        >
          {/* Page Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                Studio Invitations
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage member invitations for {studio.name}
              </Typography>
              {/* Member Usage Indicator */}
              <Box sx={{ mt: 1 }}>
                <UsageIndicator
                  type="members"
                  current={currentMemberCount}
                  limit={maxUsers}
                  showProgress
                />
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={() => setShowInviteForm(true)}
              disabled={!canAddUser}
              color={!canAddUser ? 'error' : 'primary'}
              size="large"
              sx={{
                minWidth: 160,
                ...(!canAddUser && {
                  '&:disabled': {
                    backgroundColor: 'error.light',
                    color: 'error.contrastText',
                  },
                }),
              }}
            >
              {!canAddUser ? 'Member Limit Reached' : 'Send Invitation'}
            </Button>
          </Box>

          {/* Member Limit Warning - Show when approaching limit (80%+) */}
          {!isAtUserLimit && userLimitPercent >= 80 && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <strong>Approaching Member Limit:</strong> You&apos;re using{' '}
              {Math.round(userLimitPercent)}% of your member allowance for this
              studio.
              {userLimitPercent >= 95 &&
                ' Consider upgrading your plan to add more members.'}
            </Alert>
          )}

          {/* Member Limit Reached Alert - Show when at limit */}
          {isAtUserLimit && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <strong>Member Limit Reached:</strong> You&apos;ve reached the
              maximum number of members allowed for this studio on your current
              plan.{' '}
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={() => navigate('/pricing')}
                sx={{ ml: 1 }}
              >
                Upgrade Plan
              </Button>
            </Alert>
          )}

          {/* Invitation Metrics */}
          {metrics && (
            <Card elevation={2}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: 600, mb: 3 }}
                >
                  Invitation Overview
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <PeopleIcon
                        sx={{ fontSize: 32, color: 'primary.main', mb: 1 }}
                      />
                      <Typography variant="h4" sx={{ fontWeight: 600 }}>
                        {metrics.total}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Sent
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <PendingIcon
                        sx={{ fontSize: 32, color: 'warning.main', mb: 1 }}
                      />
                      <Typography variant="h4" sx={{ fontWeight: 600 }}>
                        {metrics.pending}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pending
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <CheckCircleIcon
                        sx={{ fontSize: 32, color: 'success.main', mb: 1 }}
                      />
                      <Typography variant="h4" sx={{ fontWeight: 600 }}>
                        {metrics.accepted}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Accepted
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <TrendingUpIcon
                        sx={{ fontSize: 32, color: 'success.main', mb: 1 }}
                      />
                      <Typography variant="h4" sx={{ fontWeight: 600 }}>
                        {metrics.acceptance_rate}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Acceptance Rate
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Invitation Information */}
          <Alert severity="info" sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                How Invitations Work:
              </Typography>
              <Typography variant="body2">
                • Invitations can be sent to any email address (existing users
                or new users)
              </Typography>
              <Typography variant="body2">
                • Recipients will receive an email with a link to accept the
                invitation
              </Typography>
              <Typography variant="body2">
                • New users will create an account during the acceptance process
              </Typography>
              <Typography variant="body2">
                • <strong>Invitations expire after 90 days</strong> and will
                need to be resent
              </Typography>
            </Box>
          </Alert>

          {/* Tutorial Video */}
          <Box
            sx={{
              p: 2,
              backgroundColor: 'action.hover',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              mb: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              Watch our tutorial to learn how to invite staff members to your
              studio.
            </Typography>
            <YouTubeVideoButton
              videoId="s9RZo_gHoNQ"
              videoTitle="How to Invite Staff Members on tools.dance"
              buttonText="Watch Tutorial"
              variant="outlined"
              size="small"
            />
          </Box>

          {/* Invitations List */}
          <Card elevation={1}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, pb: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Manage Invitations
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  View, resend, or cancel pending invitations
                </Typography>
                <Divider />
              </Box>
              <Box sx={{ p: 3 }}>
                <InvitationList
                  studioId={studioId!}
                  onInvitationUpdate={handleInvitationUpdate}
                />
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Invite Member Form Dialog */}
        <InviteMemberForm
          studioId={studioId!}
          open={showInviteForm}
          onSuccess={handleInvitationSuccess}
          onCancel={() => setShowInviteForm(false)}
        />
      </Container>
    </Box>
  );
};

// Export component.
export default InvitationsPage;
